/* Select AfterSell Product add-ons once per cart, using the app's own toggle.
 * Its native handler owns line properties, totals, errors and removal. */
(() => {
  const blockSelector = '[data-aftersell-cart-block-type="aftersell_cart_product_addon_config"]';
  const toggleSelector = '.cart-external-product-addon__toggle, .cart-external-product-addon__checkbox';
  const handled = new Set();
  const offers = new Map();
  let connected = false, connecting = false, queued = false, attempts = 0;
  let api, shadow;

  // Use the site's current public graph only to recognize existing variants;
  // never copy a product, price, condition or variant ID from another store.
  try {
    const graph = JSON.parse(document.getElementById('aftersell-cart-graph')?.textContent || '{}');
    for (const view of Object.values(graph.views || {})) {
      for (const section of Object.values(view)) {
        if (!Array.isArray(section)) continue;
        for (const block of section) {
          if (block.__type !== 'aftersell_cart_product_addon_config') continue;
          const variants = (block.fields?.bindings || []).map(binding => binding.fields?.config?.fields?.variant?.id)
            .filter(Boolean).map(String);
          offers.set(String(block.id), new Set(variants));
        }
      }
    }
  } catch (error) {
    console.warn('Unable to read Product add-on configuration', error);
  }

  function identity(block, cart) {
    const id = block.getAttribute('data-aftersell-cart-block-id');
    return id && cart?.token ? `aftersell:addon-default:v1:${cart.token}:${id}` : null;
  }

  function wasHandled(key) {
    if (handled.has(key)) return true;
    try { return window.localStorage.getItem(key) === 'handled'; }
    catch { return false; }
  }

  function remember(key) {
    handled.add(key);
    try { window.localStorage.setItem(key, 'handled'); } catch {}
  }

  function selectDefault(block) {
    const cart = api.actions.getCart();
    const key = identity(block, cart);
    if (!key || wasHandled(key) || !block.isConnected || block.hidden
      || block.closest('[hidden], [aria-hidden="true"]')) return;
    const toggle = block.querySelector(toggleSelector);
    if (!toggle || toggle.disabled || toggle.getAttribute('aria-disabled') === 'true') return;
    const items = cart.items || [];
    const variants = offers.get(block.getAttribute('data-aftersell-cart-block-id'));
    if (toggle.checked || items.some(item => variants?.has(String(item.variantId)))) {
      remember(key);
      return;
    }
    const hasMainItem = items.some(item => !['product_addon', 'shipping_protection', 'rewards']
      .includes(item.properties?._aftersell_cart_module_type) && !variants?.has(String(item.variantId)));
    if (!hasMainItem) return;
    // Remember BEFORE clicking: app updates and observers can run immediately.
    // A rejected add stays under the native error/retry UI, without auto-retries.
    remember(key);
    toggle.click();
  }

  function scan() {
    queued = false;
    for (const block of shadow.querySelectorAll(blockSelector)) {
      if (navigator.locks?.request) {
        // Separate from the app's cart writer lock: the native toggle acquires
        // that lock itself. Shared storage prevents defaults racing across tabs.
        navigator.locks.request('aftersell-addon-default', () => selectDefault(block))
          .catch(error => console.warn('Product add-on default unavailable', error));
      } else selectDefault(block);
    }
  }

  function schedule() {
    if (queued) return;
    queued = true;
    queueMicrotask(scan);
  }

  async function connect() {
    if (connected || connecting) return;
    const cart = window.aftersell?.cart;
    const root = document.getElementById('aftersell-cart-root');
    if (!root?.shadowRoot || !cart?.actions?.getCart || !cart?.events?.on) return;
    connecting = true;
    try {
      if (typeof cart.ready === 'function') await cart.ready();
      api = cart;
      shadow = root.shadowRoot;
      // Capture the shopper's choice before app handlers rerender the input.
      shadow.addEventListener('change', event => {
        if (!(event.target instanceof Element) || !event.target.matches(toggleSelector)) return;
        const block = event.target.closest(blockSelector);
        const key = block && identity(block, api.actions.getCart());
        if (key) remember(key);
      }, true);
      new MutationObserver(schedule).observe(shadow, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['disabled', 'hidden', 'aria-hidden', 'aria-disabled']
      });
      for (const event of ['cart_loaded', 'cart_updated', 'cart_opened']) api.events.on(event, schedule);
      connected = true;
      clearInterval(poll);
      schedule();
    } catch (error) {
      console.warn('Product add-on default unavailable', error);
    } finally { connecting = false; }
  }

  const poll = setInterval(() => {
    if (++attempts > 300) { clearInterval(poll); return; }
    connect();
  }, 100);
})();
